import { useEffect, useRef } from 'react';
import { usePlatform } from '@/contexts/PlatformContext';
import { useCandidatePortalAccess } from '@/hooks/use-candidate-portal-access';
import { useCountdown } from '@/hooks/use-countdown';
import { Clock } from 'lucide-react';

export function PortalCountdownBanner() {
  const { portalState } = usePlatform();
  const { overrideExpiresAt } = useCandidatePortalAccess();
  const effectiveCloseDate = overrideExpiresAt ?? portalState?.closeDate ?? null;
  const countdown = useCountdown(effectiveCloseDate);
  const hasTriggeredRefreshRef = useRef(false);

  useEffect(() => {
    if (!effectiveCloseDate) return;

    const refreshKey = 'portal-close-refresh-key';
    const closeDateKey = effectiveCloseDate;
    const alreadyRefreshedForCloseDate =
      window.sessionStorage.getItem(refreshKey) === closeDateKey;

    if (alreadyRefreshedForCloseDate || hasTriggeredRefreshRef.current) return;

    const targetMs = new Date(closeDateKey).getTime();
    if (!Number.isFinite(targetMs)) return;

    const refresh = () => {
      if (hasTriggeredRefreshRef.current) return;
      hasTriggeredRefreshRef.current = true;
      window.sessionStorage.setItem(refreshKey, closeDateKey);
      window.location.reload();
    };

    const delayMs = targetMs - Date.now();
    if (delayMs <= 0) {
      refresh();
      return;
    }

    const timeoutId = window.setTimeout(refresh, delayMs + 200);
    return () => window.clearTimeout(timeoutId);
  }, [effectiveCloseDate]);

  // Only show when closeDate is in the future (we're within the open window)
  if (!effectiveCloseDate || !countdown) return null;

  const isUrgent = countdown.days === 0 && countdown.hours < 24;

  return (
    <div
      className={`
      w-full px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-medium
      ${
        isUrgent
          ? 'bg-red-100 border-b border-red-300 text-red-700 dark:bg-red-900 dark:border-red-700 dark:text-red-100'
          : 'bg-amber-100 border-b border-amber-300 text-amber-800 dark:bg-amber-900 dark:border-amber-700 dark:text-amber-100'
      }
    `}
    >
      <Clock className="w-4 h-4 shrink-0" />
      <span>
        {overrideExpiresAt ? 'Extended access ends' : 'Portal closes'}{' '}
        {countdown.days > 0 && <strong>{countdown.days}d </strong>}
        <strong>{String(countdown.hours).padStart(2, '0')}h </strong>
        <strong>{String(countdown.minutes).padStart(2, '0')}m </strong>
        <strong>{String(countdown.seconds).padStart(2, '0')}s</strong> — submit
        your application before the deadline.
      </span>
    </div>
  );
}
